import { useContext, useEffect, useState } from "react";
import { AircraftWarrantyContext } from "../../contexts/AircraftWarrantyContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import MultioptionButton from "../../../../components/Buttons/MultioptionButton";
import "./AircraftWarrantyConfiguration.css";
import { Dropdowns } from "../../../../components/Dropdowns/Dropdowns";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import Input from "../../../../components/Inputs/Input";
import {
  localStorageGet,
  localStorageSave,
} from "../../../../services/localStorageService";
import {
  getWarrantyAircraft,
  updateAircraftWarranty,
} from "../../services/AircraftConfigurationService";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { HTTP_RESPONSE_CODES } from "../../../../globals/globals";
const localStoragePrefix = "configuration/aircraft/warranty/draft/general";
const ribbonColors = ["var(--blue-300)", "var(--green-350)", "#f7931e"];

const dropdownNames = {
  38: "warrantyVendorId",
  39: "warrantyComponentVendorId",
};

const hideFieldsWhenDisabled = new Set(["Vendor"]);
const defaultEmptyValues = {
  warrantyVendorId: null,
  warrantyHours: "",
  warrantyCycles: "",
  warrantyDays: "",
  warrantyComponentVendorId: null,
  warrantyComponentHours: "",
  warrantyComponentCycles: "",
  warrantyComponentDays: "",
};
const AircraftWarrantyConfiguration = ({
  aircraft,
  disabled,
  ribbon,
  open,
}) => {
  const {
    dropdownsData,
    setDropdownsData,
    setDropdownIds,
    dropdownDefaultOptions,
  } = useContext(DropdownsContext);
  const { successMsg, warningMsg, infoMsg } = useSnackBar();
  const [partBackup, setPartBackup] = useState(null);
  const [values, setValues] = useState({ ...defaultEmptyValues });
  const [defaults, setDefaults] = useState({});
  const [backup, setBackup] = useState(null);
  const handleDropdownValue = (value, name, data) => {
    changeValue({ target: { name, value: value[data], completeValue: value } });

    setDefaults((prev) => ({
      ...prev,
      ...{ [name]: " " },
    }));
  };
  const updateDropdown = (dropdownId, parentValues, dependenciesToUpdate) => {
    setDropdownsData((prevDropdowns) =>
      prevDropdowns.map((filter) => {
        if (filter.id === dropdownId) {
          return {
            ...filter,
            parentValues,
            dependenciesToUpdate,
            reload: true,
          };
        }
        return filter;
      })
    );
  };
  useEffect(() => {
    if (disabled && typeof values.id === "number") {
      localStorageSave(localStoragePrefix, { id: aircraft.id }, values);
    }
  }, [values]);

  useEffect(() => {}, [open, aircraft]);
  useEffect(() => {
    if (open) {
      getWarrantyAircraft(aircraft.id)
        .then((response) => {
          if (response.data.responseCode === 200) {
            if (response.data.load != null) {
              let {
                id,
                warrantyVendorId,
                warrantyHours,
                warrantyCycles,
                warrantyDays,
                warrantyComponentVendorId,
                warrantyComponentHours,
                warrantyComponentCycles,
                warrantyComponentDays,
                version
              } = response.data.load;
              setPartBackup(response.data.load);
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                id,
                warrantyVendorId: warrantyVendorId,
                warrantyHours: warrantyHours,
                warrantyCycles: warrantyCycles,
                warrantyDays: warrantyDays,
                warrantyComponentVendorId: warrantyComponentVendorId,
                warrantyComponentHours: warrantyComponentHours,
                warrantyComponentCycles: warrantyComponentCycles,
                warrantyComponentDays: warrantyComponentDays,
                version: version,
                aircraftId: { id: aircraft.id },
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
              setBackup(form);
            } else {
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                aircraftId: aircraft.id,
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
            }
          }
        })
        .catch((error) => {
          console.error(error);
          let form = { ...defaultEmptyValues };
          setDefaults(form);
          setValues(form);
        });
    }
  }, [open, aircraft]);
  const resetDropdownsReload = () => {
    setDropdownsData((filterState) => {
      if (filterState.length > 0) {
        return filterState.map((e) => {
          if (e.reload) {
            return { ...e, reload: false };
          }
          return e;
        });
      }
      return filterState;
    });
  };
  const cancelEdit = () => {
    removeDraft(aircraft.id, "general");
    setValues(backup ?? defaultEmptyValues);
    setDefaults(backup ?? defaultEmptyValues);
  };
  const changeValue = (e) => {
    const { name, value, completeValue } = e.target;
    setValues((prev) => {
      return {
        ...prev,
        ...{ [name]: value },
      };
    });
  };
  const generalSectionButtons = [
    {
      label: "Save and Finish",
      onClick: () => saveSection(true),
      icon: "icon-save",
      id: "save",
    },
    {
      label: "Save and Continue Editing",
      onClick: () => saveSection(false),
      icon: "icon-save",
      id: "save-continue",
    },
    {
      label: "Cancel",
      onClick: () => cancelEdit(),
      icon: "icon-cross",
      id: "cancel",
      type: "danger",
    },
  ];
  const enginesNumber = 2;
  const doNothing = () => {};
  const { hasPermission } = usePermissions();
  const {
    saveDraft,
    removeDraft,
    resetType,
    newDrafts,
    isAircraftSectionDraft,
    updateSection,
  } = useContext(AircraftWarrantyContext);

  const saveSection = (disableForm) => {
    infoMsg("Saving...");
    // let draft = JSON.parse(localStorage.getItem(`${localStoragePrefix}?id=${part.id}`) ?? "{}");
    let draft = localStorageGet(localStoragePrefix, {
      id: aircraft.id.toString(),
    })[0];

    draft.aircraftId = { id: aircraft.id };

    updateAircraftWarranty(draft)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.UPDATED) {
          successMsg("Aircraft saved successfully");
          setValues({ ...defaultEmptyValues, ...response.data.load });
          setBackup({ ...defaultEmptyValues, ...response.data.load });
          if (disableForm) {
            removeDraft(aircraft.id, "general");
          }
        } else if (
          response.data.responseCode === HTTP_RESPONSE_CODES.VALIDATIONS_FAILED
        ) {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            "One or more fields are invalid.",
            30000
          );
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            100000
          );
        }
      })
      .catch((error) => warningMsg(`Something went wrong: ${error}`));
  };
  return (
    <div className="general-aircraft-section-form-wrapper">
      <div className="general-section-aircraft-form-component">
        <div
          className="general-section-aircraft-form-header"
          style={{ marginBottom: "0.875rem" }}
        >
          {ribbon && (
            <div className="ribbons-container">
              {ribbon
                .filter?.((r) => r.when)
                ?.map?.((r, i, a) => (
                  <span
                    className="ribbon"
                    key={r.label}
                    style={{
                      backgroundColor:
                        r.color ?? ribbonColors[i % ribbonColors.length],
                      left:
                        i < a.length - 1
                          ? `${-0.5 * (ribbon.length - 1 - i)}rem`
                          : "",
                    }}
                  >
                    {r.label}
                  </span>
                ))}
            </div>
          )}
          <div className="title-container">
            <b>Warranty</b>
          </div>
          <div className="general-info-actions">
            {!disabled && hasPermission("update") && (
              <button
                className="outlined iconTextButton small-size"
                onClick={() => saveDraft(aircraft.id, "general", values)}
              >
                <i className="icon-edit"></i>
                <span>Edit</span>
              </button>
            )}
            {disabled && hasPermission("update") && (
              <MultioptionButton buttons={generalSectionButtons} />
            )}
          </div>
        </div>
        <b>AC Warranty</b>
        <div className="general-section-aircraft-warranty">
          <Dropdowns
            context={{
              dropdownsData,
              setDropdownsData,
              setDropdownIds,
              dropdownDefaultOptions,
            }}
            updateDropdown={updateDropdown}
            resetDropdownsReload={resetDropdownsReload}
            hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[38])}
            disabled={!disabled}
            key={38}
            onOptionSelect={(value, data) =>
              handleDropdownValue(value, dropdownNames[38], data)
            }
            showPagination={false}
            data={dropdownsData.find((e) => e.id === 38)}
            preSelectValue={defaults?.[dropdownNames[38]]}
          />
          <div className="general-aircraft-form">
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              disableAutocomplete={true}
              name="warrantyHours"
              label={"Hours"}
              isNumeric={true}
              value={values["warrantyHours"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              disableAutocomplete={true}
              name="warrantyCycles"
              label={"Cycles"}
              isNumeric={true}
              value={values["warrantyCycles"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              disableAutocomplete={true}
              name="warrantyDays"
              isNumeric={true}
              label={"Days"}
              value={values["warrantyDays"]}
            />
          </div>
        </div>
        <b>AC Component Warranty</b>
        <div className="general-section-aircraft-warranty">
          <Dropdowns
            context={{
              dropdownsData,
              setDropdownsData,
              setDropdownIds,
              dropdownDefaultOptions,
            }}
            updateDropdown={updateDropdown}
            resetDropdownsReload={resetDropdownsReload}
            hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[39])}
            disabled={!disabled}
            key={39}
            onOptionSelect={(value, data) =>
              handleDropdownValue(value, dropdownNames[39], data)
            }
            showPagination={false}
            data={dropdownsData.find((e) => e.id === 39)}
            preSelectValue={defaults?.[dropdownNames[39]]}
          />
          <div className="general-aircraft-form">
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="warrantyComponentHours"
              label={"Hours"}
              isNumeric={true}
              disableAutocomplete={true}
              value={values["warrantyComponentHours"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="warrantyComponentCycles"
              label={"Cycles"}
              isNumeric={true}
              disableAutocomplete={true}
              value={values["warrantyComponentCycles"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="warrantyComponentDays"
              label={"Days"}
              isNumeric={true}
              disableAutocomplete={true}
              value={values["warrantyComponentDays"]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftWarrantyConfiguration;
